import { wait } from './utilsService';
import nftImage from '../images/nft-image.jpeg';
import configJson from '../config/config.json';
import { handleResponse } from './apiService';
import { getChampionOwnerData, getChampionToken } from './championService';
import {
  HordConfigurationContract,
  HordTicketFactoryContract,
  HPoolManagerContract,
  HordTicketManagerContract,
} from './contractsRegistryService';
import { assetAmountInEth, safeDivideOnWei } from './assetsService';
import { getAccount } from './walletService';
import {
  createNewContract,
  getContractAddress,
  getContractInstance,
  newContractReader,
  useContractReader,
} from './contractReader';
import { hpoolPublicSubscribe, parseInputs, parseInputsByAbi } from './hpoolService';
import { getTxInfo, ERROR_PROP } from './tenderlyService';
import { divideOnWei, getValueByMultiPropsLowerCased, multiConver } from '../utils/mapper';
import { FeesTakenAbi, IndividualLiquidationAbi } from '../constants/createHPool';
import { displayFloats } from '../utils/numbersDisplay';
import { displaySmallFLoats, _toFixed } from '../components/Common/SliderTokensList/SliderToken/TokenPrice/TokenPrice';
import { getTokenSymbol } from '../utils/format';
import { multiTypePoolManager } from '../constants/vpool';

/**
 * @typedef Token
 * @property {number} id
 * @property {string} network
 * @property {string} symbol
 * @property {string} address
 * @property {number} decimals
 * @property {string} image
 * @property {string} name
 * @property {Array} verified_token_lists
 */

/**
 * Gets tokens list
 * @return {Promise<Array.Token>}
 */
export const getTokensList = (search) => {
  const url = new URL(`${configJson.apiPath}/token/list`);

  if (search) {
    url.searchParams.append('q', search);
    url.searchParams.append('chain_id', window.chainId || configJson.network);
  }
  return fetch(url)
    .then(handleResponse)
    .then(({ tokens }) => tokens);
};

const getWrappedToken = (tokenSymbol = '') => `w${tokenSymbol.toLowerCase()}`;

export const getBaseToken = (addedTokens = [], symbol = '') => {
  const isArray = Array.isArray(symbol);
  if (isArray) {
    const symbols = symbol; // it's an array now
    const found = symbols.find((singleSymbol) => getBaseToken(addedTokens, singleSymbol));
    if (found) return getBaseToken(addedTokens, found);
  }
  const { configTokens = [] } = window;

  if (!configTokens.length) return {};
  const mainTokenSymbol = getTokenSymbol(configTokens[0]);

  const wrappedMainToken = getWrappedToken(mainTokenSymbol);
  const baseTokens = [mainTokenSymbol, wrappedMainToken];

  const baseTokenResource = !symbol ? baseTokens : [symbol];
  const baseToken = addedTokens.find((el) =>
    baseTokenResource.includes(getValueByMultiPropsLowerCased(el, ['token_symbol', 'symbol'])),
  );
  return baseToken;
};

const handleArrayBaseToken = (symbols) => {};

export const mixHPoolAssets = (existingAssets = [], hpoolAssets = []) => {
  try {
    if (!existingAssets.length) return [];
    const mainTokenSymbol = getTokenSymbol(existingAssets[0]);
    if (!hpoolAssets.length) return existingAssets;
    const existingAssetsObj = existingAssets.reduce((acc, curr) => {
      acc[curr.token_id] = curr;
      return acc;
    }, {});
    // TODO remove hacks with remapping values once we get from BE
    const mixedAssets = hpoolAssets.map((asset) => ({
      ...(existingAssetsObj[asset.token_id] || {}),
      ...asset,
      percentage: asset.current_ratio_from_pool,
      verified_token_lists: asset.verified_token_lists || [],
      network: asset.network || configJson.baseCoin.toLowerCase(),
      // image: asset.image || asset.token_image,
    }));

    // TODO remove next line once BE will return assets without duplicates
    const baseToken = getBaseToken(mixedAssets, mainTokenSymbol);
    const hordToken = getBaseToken(mixedAssets, 'hord');
    const sortedAssets = [
      baseToken,
      hordToken,
      ...mixedAssets.filter((asset) => ![baseToken.token_id, hordToken.token_id].includes(asset.token_id)),
    ];
    console.log('MIXED_ASSETS', {
      mixedAssets,
      existingAssetsObj,
      hpoolAssets,
      sortedAssets,
    });
    return sortedAssets;
  } catch (error) {
    console.log();
  }
};

const createHPool = async (body, isVpool) => {
  const mergedWithWallet = {
    ...body,
    champion_address: await getAccount(),
    is_vpool: isVpool,
  };
  const config = {
    method: 'POST',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify(mergedWithWallet),
  };
  return fetch(`${configJson.apiPath}/hpool`, config).then(handleResponse);
};

export const updateHPool = (body) => {
  const updatedBody = { ...body };
  delete updatedBody['hpool_id'];
  delete updatedBody['id'];
  const config = {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify(updatedBody),
  };
  return fetch(`${configJson.apiPath}/hpool/${body.id}`, config).then(handleResponse);
};

export const fetchMostUsedTokensRequest = () => {
  const url = `${configJson.apiPath}/token/list/used?chain_id=${window.chainId || configJson.network}`;
  return fetch(url).then(handleResponse);
};

export const getDraftHPoolForChampion = () => {
  const config = {
    method: 'GET',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
  };

  const url = new URL(`${configJson.apiPath}/hpool`).href;
  return fetch(url, config).then(handleResponse);
  // .then((response) => {
  //   if (response?.hpool?.id) {
  //     return fetch(`${configJson.apiPath}/hpool/${response?.hpool?.id}`, config).then(handleResponse);
  //     // .then((hPoolRes) => ({ ...response, ...hPoolRes }));
  //   }
  //   return response;
  // });
};

export const getChampion = (championId) => {
  const config = {
    method: 'GET',
    headers: { Authorization: 'Bearer GUEST' },
  };

  const url = new URL(`${configJson.apiPath}/champion?champion_id=${championId}`);
  return fetch(url, config).then(handleResponse);
};

const appendChampionData = (hpools) =>
  Promise.all(
    hpools.map(async (hPool) => {
      const championData = await getChampion(hPool.champion_id);

      return {
        ...hPool,
        champion: {
          profileImg: championData.champion.profile_media_url,
          name: championData.champion.name,
          twitterHandle: championData.champion.handle,
        },
      };
    }),
  );

export const getHPools = async () => {
  const config = {
    method: 'GET',
    headers: { Authorization: 'Bearer GUEST' },
  };

  const url = new URL(`${configJson.apiPath}/hpool/list`);
  const { hpools } = await fetch(url, config).then(handleResponse);

  return hpools;
};

export const getChampionUnattachedNFT = (championId) => {
  const url = new URL(`${configJson.apiPath}/champion/nft`);
  url.searchParams.append('unattached', 'true');
  url.searchParams.append('champion_id', championId.toString());
  url.searchParams.append('chain_id', window.chainId);
  return fetch(url).then(handleResponse);
};

export const updateChampionUnattachedNFT = (body) => {
  const url = new URL(`${configJson.apiPath}/champion/nft`);
  const config = {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify(body),
  };

  return fetch(url, config).then(handleResponse);
};

export const assignNFTToHPool = (body) => {
  const url = new URL(`${configJson.apiPath}/hpool_nft/${body.nft_id}`);
  const config = {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify({ hpool_id: body.hpool_id }),
  };

  return fetch(url, config).then(handleResponse);
};

export const createOrUpdateHPool = (body, isVpool) => (body.id ? updateHPool(body) : createHPool(body, isVpool));

export const findTokenToAddConditional = (newToken, addedToken) => {
  const key = Object.keys(newToken).find((key) => /^id|^token_id/.test(key));
  return newToken[key] === addedToken[key];
};

const getLockedUnlockedTokensPercentagesXVal = (tokensArr) => {
  const [lockedPercentage, unlockedPercentage] = tokensArr.reduce(
    (_acc, { percentage, locked, changed }) => {
      const acc = [..._acc];

      if (locked || changed) acc[0] += percentage;
      else acc[1] += percentage;
      return acc;
    },
    [0, 0],
  );
  const remainingPercentage = 100 - lockedPercentage;
  const res = remainingPercentage / unlockedPercentage;
  // check if it is infinity or NaN, for example 0/0 or 1/0

  if (Number(res) !== res) return 1;
  return res;
};

const getTetherToken = (addedTokens) => {
  const tetherToken = addedTokens.find((el) => el.name === 'Tether');
  return tetherToken;
};

const countTotalPercentage = (tokens) => tokens.reduce((accumulator, curToken) => accumulator + curToken.percentage, 0);

const roundTokensOnPercentChange = (_tokensArr) => {
  const tokensArr = [..._tokensArr];

  // sort the tokens by the remainder
  const sortedRemainder = [...tokensArr].sort((token1, token2) => (token2.percentage % 1) - (token1.percentage % 1), 0);
  // check if the biggest remainder exists
  const firstSortedTokenRemainder = sortedRemainder[0].percentage % 1;
  const hasToRound = firstSortedTokenRemainder < 1 && firstSortedTokenRemainder > 0;
  let deviation = 0;
  const newArr = tokensArr.map((token) => {
    // check if there is a token with a remainder

    let newPercentage;
    if (hasToRound) {
      const sortedIndex = sortedRemainder.findIndex((sortedToken) => findTokenToAddConditional(token, sortedToken));
      // find the token with the highest remainder and round it up
      if (sortedIndex === 0) {
        newPercentage = Math.ceil(token.percentage);
        deviation += newPercentage - token.percentage;

        return { ...token, percentage: newPercentage };
      }
    }

    // round all other tokens down
    newPercentage = Math.floor(token.percentage);
    deviation += newPercentage - token.percentage;
    return { ...token, percentage: newPercentage };
  });

  deviation = Math.round(deviation);
  if (deviation !== 0) {
    const ceiledToken = newArr.find((token) => findTokenToAddConditional(sortedRemainder[0], token));
    ceiledToken.percentage -= deviation;
  }

  return newArr;
};

const changeAllTokensPercentage = (addedTokens) => {
  const copyAddedTokens = [...addedTokens];

  // Get the middle ratio value to change the other tokens
  const xValue = getLockedUnlockedTokensPercentagesXVal(copyAddedTokens);

  // Change tokens which were not the slided one
  const changed = copyAddedTokens.map((addedToken) => {
    if (addedToken.changed) return { ...addedToken, changed: false };
    if (addedToken.locked) return addedToken;

    return { ...addedToken, percentage: addedToken.percentage * xValue };
  });

  const roundedToken = roundTokensOnPercentChange(changed);
  const totalPercentage = countTotalPercentage(roundedToken);
  // if (totalPercentage !== 100) {
  //   console.log('ERRROR: TOTAL SUM IS NOT 100');
  //   console.log(totalPercentage);
  // }
  return roundedToken;
};

export const createHPoolHandleTokenPercentageChange = (changedToken, newVal, allTokens, newlyAddedToken = false) => {
  // Changed 1-level shallow copy to deep copy
  // const addedTokens = [...allTokens];
  const addedTokens = JSON.parse(JSON.stringify(allTokens));
  const { configTokens } = window;
  const baseTokenName = configTokens[0]?.symbol?.toLocaleLowerCase?.() || configJson.baseCoin.toLocaleLowerCase();
  const baseToken = getBaseToken(addedTokens, baseTokenName);

  // Change the slided token percentage
  const changedTokenIndex = addedTokens.findIndex((addedToken) => findTokenToAddConditional(changedToken, addedToken));
  const changedTokenNew = addedTokens[changedTokenIndex];
  changedTokenNew.percentage = newVal;
  changedTokenNew.changed = true;
  // .changed is set so that the percentage change for it is regarded as locked in getLockedUnlockedTokensPercentagesXVal
  const diff = newlyAddedToken ? 1 : newVal - changedToken.percentage; // Newly added tokens percentage gets set to 1

  // if (baseTokenName !== changedTokenName) {
  //   const changedTokens = changeAllTokensPercentage(addedTokens);
  //   return changedTokens;
  // }

  if (diff !== 0) {
    // when incresing tokens'value, decrease eth value
    baseToken.changed = true;
    const min = baseToken.min || 4;

    if (baseToken.percentage - diff > min) {
      baseToken.percentage -= diff;
    } else {
      const leftover = diff - (baseToken.percentage - min);
      baseToken.percentage = min;
      changedTokenNew.percentage = newVal - leftover;

      // ethereumToken.locked = true;
      // ethereumToken.changed = false;
      // ethereumToken.percentage = ethereumToken.min;
      //
      // const changedTokens = changeAllTokensPercentage(addedTokens);
      // const changedEthereumToken = getEthereumToken(changedTokens);
      // changedEthereumToken.locked = false;
      // return changedTokens;
    }
  }
  return addedTokens;
};

export const createHPoolHandleTokenDelete = (deletedToken, _addedTokens) => {
  // const copiedDeletedToken = { ...deletedToken };

  const ethereumToken = getBaseToken(_addedTokens);

  // const remainderEthPercentage = ethereumToken.percentage - ethereumToken.min;
  // copiedDeletedToken.percentage -= remainderEthPercentage;
  // ethereumToken.percentage = ethereumToken.min;
  // ethereumToken.locked = true;

  ethereumToken.percentage += deletedToken.percentage;
  // Filter out the deleted token
  const addedTokens = [..._addedTokens].filter((addedToken) => !findTokenToAddConditional(deletedToken, addedToken));

  // const changedTokens = changeAllTokensPercentage(addedTokens);
  // const changedEthereumToken = getEthereumToken(changedTokens);
  // changedEthereumToken.locked = false;
  return addedTokens;
};

export const getChampionNftImage = () => nftImage;

export const submitCreateHPool = () => wait(2000, 'new-pool');

export const getTagsUrl = (tag) => new URL(`${configJson.apiPath}/tags?q=${tag}`).href;

export const getTags = (tag) => {
  const config = {
    method: 'GET',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
  };

  const url = getTagsUrl(tag);
  return fetch(url, config).then(handleResponse);
};

export const getHPool = (id) => {
  const authToken = getChampionToken() || 'GUEST';
  const config = {
    method: 'GET',
    headers: { Authorization: `Bearer ${authToken}` },
  };

  const url = new URL(`${configJson.apiPath}/hpool/${id}`);
  return fetch(url, config).then(handleResponse);
};

export const depositHPool = async ({ id, amountWei, account, onTxHashCallback, isVpool }) => {
  const sentValue = isVpool ? {} : { value: amountWei };
  return newContractReader({
    contractName: multiTypePoolManager(isVpool).contractName,
    functionName: 'createPool',
    params: [id],
    onTxHash: onTxHashCallback,
    sendParams: { from: account, ...sentValue },
  });
};
// new Promise((resolve, reject) => {
//   useContractReader(multiTypePoolManager(isVpool), 'createPool', [id], (contractInstance) => {
//     contractInstance.methods
//       .createPool(id)
//       .send({ from: account, value: amountWei })
//       .once('transactionHash', onTxHashCallback)
//       .once('receipt', (receipt) => {
//         console.log('depositHPool_receipt', receipt);
//         resolve(receipt);
//       })
//       .on('error', (err) => {
//         reject(err);
//       });
//   });
// });
// newContractReader({
//   contractName: multiTypePoolManager(isVpool).contractName,
//   functionName: 'createPool',
//   sendParams: { from: account, value: amountWei },
//   params: [id],
// });

// new Promise((resolve, reject) => {
//   useContractReader(multiTypePoolManager(isVpool).contractName, 'createPool', [id], (contractInstance) => {
//     contractInstance.methods
//       .createPool(id)
//       .send({ from: account, value: amountWei })
//       .once('transactionHash', onTxHashCallback)
//       .once('receipt', (receipt) => {
//         console.log('depositHPool_receipt', receipt);
//         resolve(receipt);
//       })
//       .on('error', (err) => {
//         reject(err);
//       });
//   });
// });

const checkHasApprovedAll = async (account) => {
  const hpoolManagerAddress = await getContractAddress('HordHpoolManager');
  return useContractReader('HordTicketFactory', 'isApprovedForAll', [account, hpoolManagerAddress]);
};

const handlePrivateSubscription = async (isPrivate) => {
  const account = await getAccount();
  const HPoolManagerAddress = await getContractAddress('HordHpoolManager');
  const hasApprovedForAll = await checkHasApprovedAll(account);
  return new Promise((resolve, reject) => {
    // you don't have to setApprovalForAll in case it's public stage or you already approved
    if (!isPrivate || hasApprovedForAll) {
      resolve(true);
      return;
    }
    useContractReader('HordTicketFactory', 'setApprovalForAll', null, (contractInstance) => {
      contractInstance.methods
        .setApprovalForAll(HPoolManagerAddress, true)
        .send({ from: account })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  });
};

export const getMinAmountToStake = () => useContractReader('HordConfiguration', 'minAmountToStake');
// export const getMinAmountToStake = () => useContractReader('HordTicketManager', 'minAmountToStake');
export const getmaxUSDAllocationPerTicket = () => useContractReader('HordConfiguration', 'maxUSDAllocationPerTicket');

export const getMethodInput = async (contractName, contractMethod, _params = []) => {
  const contract = await getContractInstance(contractName);
  const params = [_params].flat();
  const func = contract.methods?.[contractMethod];
  const input = func(...params).encodeABI();
  return input;
};

export const subscribeHPool = async (id, amountWei, account, onTxHashCallback = () => {}, type = 'private') => {
  const subscriptionMethod = type === 'private' ? 'privateSubscribeForHPool' : 'publicSubscribeForHPool';
  const isPrivate = type === 'private';
  return new Promise((resolve, reject) => {
    handlePrivateSubscription(isPrivate).then(() => {
      useContractReader('HordHpoolManager', null, null, (contractInstance) => {
        try {
          contractInstance.methods?.[subscriptionMethod](id)
            .send({ from: account, value: amountWei })
            .once('transactionHash', () => {})
            .once('receipt', (receipt) => {
              const { transactionHash } = receipt || {};
              console.log('depositHPool_receipt', receipt);
              resolve(transactionHash);
            })
            .on('error', async (err = {}) => {
              const { message } = err;
              // prevent showing tall message errors
              if (message.length < 100) reject(message);
              else reject('Error in Transaction');
            });
        } catch (err) {
          if (err) reject(err);
        }
      });
    });
  });
};

export const getHpoolInfo = (web3HpoolId, isVpool, chainId) =>
  newContractReader({
    contractName: multiTypePoolManager(isVpool).contractName,
    functionName: 'getPoolInfo',
    params: [web3HpoolId],
    chainId,
  });

export const getSummaryHpoolInfo = async (hpoolAddress) => {
  const hpoolInfo = await useContractReader('HPool', 'getHPoolInfo', undefined, undefined, hpoolAddress);
  return hpoolInfo;
};

export const getBalanceOfHpool = async (hpoolAddress) => {
  const hpooBalance = await useContractReader('HPool', 'balanceOf', [hpoolAddress], undefined, hpoolAddress);
  return hpooBalance;
};

export const getHpoolReservedTickets = async (web3HpoolId) => {
  const contract = await HPoolManagerContract();
  const reservedTickets = await useContractReader(contract, 'getNumberOfTicketsUsed', [web3HpoolId]);
  return reservedTickets;
};

export const claimMyTokens = async (hpoolAddress, account) =>
  newContractReader({
    contractName: 'HPool',
    functionName: 'claimHPoolTokens',
    contractAddress: hpoolAddress,
    sendParams: { from: account },
  });

export const getHpoolSubscribers = async (web3HpoolId) => {
  const subscriberAddresses = await useContractReader('HordHpoolManager', 'getSubscribedAddresses', [
    web3HpoolId,
    1,
    5,
  ]);
  return subscriberAddresses;
};

export const getMinimalETHToInitPool = async () => {
  const minETHAmount = await useContractReader('HordHpoolManager', 'getMinimalETHToInitPool');
  return assetAmountInEth(minETHAmount);
};

export const getMinimalETHToSubscribePool = async () => {
  const minETHAmount = await useContractReader('HordHpoolManager', 'getMinSubscriptionToLaunchInETH');
  return assetAmountInEth(minETHAmount);
};

export const getMinDepositValue = async () => useContractReader('HordConfiguration', 'minChampStake');

export const getTicketsInfo = async (nftToken) => {
  // minPoolAllocation - maxPoolAllocation - allocationPerTicket - number of ticket
  const maxPoolAllocation = newContractReader({
    contractName: 'HordConfiguration',
    functionName: 'maxFollowerUSDStake',
  });
  const minPoolAllocation = newContractReader({
    contractName: 'HordConfiguration',
    functionName: 'minFollowerUSDStake',
  });
  const allocationPerTicket = newContractReader({
    contractName: 'HordConfiguration',
    functionName: 'maxUSDAllocationPerTicket',
  });
  const numberOfTickets = newContractReader({
    contractName: 'HordTicketFactory',
    functionName: 'getTokenSupply',
    params: [nftToken],
  });
  return Promise.allSettled([minPoolAllocation, maxPoolAllocation, allocationPerTicket, numberOfTickets]);
};

window.getMinimalETHToInitPool = getMinimalETHToInitPool;

// / compares the previous tokens to the updated ones, sees if there is any change
// // change token_id to id or id to token_id ??? Currently it uses name for id

export const checkTokenChanges = (prevTokens, updatedTokens) => {
  const updatedTokenArray = [];
  for (let i = 0; i < prevTokens.length; i += 1) {
    const token = updatedTokens.find((el) => el.name === prevTokens[i].name);

    if (!token) {
      updatedTokenArray.push({
        tokenId: prevTokens[i].name,
        tokenName: prevTokens[i].name,
        network: prevTokens[i].network,
        image: prevTokens[i].image,
        symbol: prevTokens[i].symbol,
        status: 'Removed',
      });
    } else {
      updatedTokenArray.push({
        tokenId: prevTokens[i].name,
        prevPercentage: prevTokens[i].percentage,
        updatedPercentage: token.percentage,
        tokenName: prevTokens[i].name,
        network: prevTokens[i].network,
        image: prevTokens[i].image,
        symbol: prevTokens[i].symbol,
        status: 'Updated',
      });
    }
  }

  for (let k = 0; k < updatedTokens.length; k += 1) {
    const token = prevTokens.find((el) => el.name === updatedTokens[k].name);
    if (!token) {
      updatedTokenArray.push({
        tokenId: updatedTokens[k].name,
        updatedPercentage: updatedTokens[k].percentage,
        tokenName: updatedTokens[k].name,
        network: updatedTokens[k].network,
        image: updatedTokens[k].image,
        symbol: updatedTokens[k].symbol,
        status: 'Added',
      });
    }
  }

  return updatedTokenArray;
};

export const createHpoolContract = async (address) => {
  const _contract = await createNewContract('HPool', address);
  const contract = await _contract();
  return contract;
};

const toLowerCaseArr = (arr = ['']) => arr.map((elem) => elem?.toLowerCase?.());

export const getIndividualLiquidation = async ({
  poolContract,
  liquidateTxHash,
  event = 'IndividualLiquidation',
  abi = IndividualLiquidationAbi,
  converter = (value) => value,
}) => {
  const hpoolContract = await createHpoolContract(poolContract);
  const { arguments: eventArgs = [] } = (await hpoolContract.events?.[event]?.()) || {};
  const [firstArg = {}] = eventArgs;

  const { topics: eventTopics } = firstArg;
  const [firstTopic = ''] = eventTopics;

  const { logs: txLogs = [] } = (await window._web3.eth.getTransactionReceipt(liquidateTxHash)) || {};
  const foundLog = txLogs.find((log) => toLowerCaseArr(log.topics).includes(firstTopic.toLowerCase())) || {};

  const { data: logData } = foundLog;
  const txInputs = await parseInputsByAbi(abi, logData);

  const afterFormat = converter(txInputs);
  return afterFormat;
};

export const getTransactionBlock = async (txHash) => {
  const res = (await window._web3.eth.getTransactionReceipt(txHash)) || {};
  const { blockNumber: txBlockNumber } = res;
  const { number: currentBlockNumber } = (await window._web3.eth.getBlock('latest')) || {};
  return { txBlockNumber, currentBlockNumber };
};

export const getFeesAndRedeemInfo = async ({ poolContract, liquidateTxHash }) => {
  const feesInfo = await getIndividualLiquidation({
    poolContract,
    liquidateTxHash,
    event: 'FeesTaken',
    abi: FeesTakenAbi,
    converter: (txInputs) =>
      multiConver([{ keys: Object.keys(txInputs), convert: (value) => _toFixed(safeDivideOnWei(value)) }], txInputs),
  });
  const redeemInfo = await getIndividualLiquidation({
    poolContract,
    liquidateTxHash,
    event: 'IndividualLiquidation',
    abi: IndividualLiquidationAbi,
    converter: (txInputs) =>
      multiConver(
        [{ keys: Object.keys(txInputs), convert: (value) => displaySmallFLoats(divideOnWei(value), 3) }],
        txInputs,
      ),
  });
  return { ...feesInfo, ...redeemInfo };
};

export const getPoolSafts = (poolId) => {
  const config = {
    method: 'GET',
    headers: { Authorization: 'Bearer GUEST' },
  };

  const url = new URL(`${configJson.apiPath}/hpool/${poolId}/target-safts`);
  return fetch(url, config)
    .then(handleResponse)
    .then(({ target_safts: targetSafts }) => targetSafts);
};

export const createPoolSafts = (poolId, body) => {
  const url = new URL(`${configJson.apiPath}/hpool/${poolId}/target-safts`);
  const config = {
    method: 'POST',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify(body),
  };

  return fetch(url, config).then(handleResponse);
};

export const updatePoolSafts = (poolId, body, saftId) => {
  const url = new URL(`${configJson.apiPath}/hpool/${poolId}/target-safts/${saftId}`);
  const config = {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify(body),
  };

  return fetch(url, config).then(handleResponse);
};
